import {
  ArchiveIcon,
  ChevronUpIcon,
  MixerHorizontalIcon,
  Pencil1Icon,
  TrashIcon,
} from "@radix-ui/react-icons";
import {
  AlertDialog,
  Badge,
  Button,
  Card,
  ChevronDownIcon,
  Flex,
  Text
} from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { Lists } from "../../components/common/Lists";
import TableComponent from "../../components/common/Tables";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetAcademicYearQuery } from "../../hooks/useGetAcademicYearQuery";
import { useGetCoursesQuery } from "../../hooks/useGetCoursesQuery";
import { useGetSubscriptionsQuery } from "../../hooks/useGetSubscriptionsQuery";
import { setPdfPreviewType } from "../../redux/slices/report/report";
import { setStudentData } from "../../redux/slices/school/student";
import { setSubscriptionData } from "../../redux/slices/school/subscription";
import { setUserData } from "../../redux/slices/users/user";
import { routes } from "../../routes";
import { getStudentDebtsBySubscription } from "../../services/finance/studentDebt";
import { getStudentById } from "../../services/school/student/getStudentByCode";
import { getSubjectsSubscriptions } from "../../services/school/student/getSubscriptions";
import { updateSubscription } from "../../services/school/student/updateSubscription";
import { getUserById } from "../../services/user/getUsers";
import { SubscriptionType } from "../../types/Student";
import {
  Key_subscriptions,
  PAGE_NUMBER,
  PAGE_SIZE,
} from "../../utils/constants";
import { stringToBoolean } from "../../utils/utilities";
import { resetLoaderAvailable, setLoaderAvailable } from "../../redux/slices/loaderController";
import { useLogoBase64 } from "../../hooks/useLogoBase64";
import { useIES } from "../../hooks/useIES";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useExportDocument } from "../../hooks/useExportDocument";

const columnNamesMap: { [key: string]: string } = {
  student: "Estudante",
  studentCode: "Código",
  course: "Curso",
  academicYear: "Ano lectivo",
  level: "Ano",
  semester: "Semestre",
};

interface Filters {
  course: string;
  academicYear: string;
  semester: number | null;
  level: number | null;
}

export const Subscriptions = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [itemToBeDeleted, setItemToBeDeleted] =
    useState<SubscriptionType | null>(null);

  const { logoBase64 } = useLogoBase64();
  const { IES } = useIES();
  const loginData = useSelector((state: RootState) => state.login.userData);
  const { exportDocumentPdf: exportDocument, downloadOnly } = useExportDocument();

  type Filters = {
    IdCourse?: string;
    IdAcademicYear?: string;
    Level?: number;
    Semester?: number;
  }
  const {
    control,
    formState: { errors },
    getValues, setValue,
    watch,
    reset
  } = useForm<Filters>({
    defaultValues: { IdCourse: "", IdAcademicYear: "", Level: 0, Semester: 0 },
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUseNativeValidation: false,
  });
  const { data: academicYears, isLoading: isLoadingAcademicYears, isError: errorAcademicYears } = useGetAcademicYearQuery({ PageNumber: 1, PageSize: 400 });
  const filters = watch();

  const { data, status } = useGetSubscriptionsQuery({
    PageNumber: currentPage,
    PageSize: pageSize,
    SearchTerm: searchTerm, ...filters
  });

  const { data: courses, isLoading: isLoadingCourses, isError: errorCourses, refetch: refreshCourses } = useGetCoursesQuery({ PageNumber: 1, PageSize: 400 },
  );

  const [showFilters, setShowFilters] = useState(false);

  const mutationDelete = useMutation(updateSubscription, {
    onSuccess: () => {
      queryClient.invalidateQueries(Key_subscriptions);
      toast.success("Apagado");
    },
    onError: () => {
      toast.error("Erro ao apagar.");
    },
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(PAGE_NUMBER);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleClickCreate = (event: any): void => {
    navigate(routes.createSubscription);
  };

  const openDeleteDialog = (s: SubscriptionType) => {
    setItemToBeDeleted(s);
  };

  const confirmDelete = () => {
    if (itemToBeDeleted) {
      const updatedSubscription = { ...itemToBeDeleted, status: false };
      mutationDelete.mutateAsync(updatedSubscription);
      setItemToBeDeleted(null);
    }
  };
  const handlePrintSubscription = async (subscription: SubscriptionType) => {
    dispatch(setLoaderAvailable(true));

    const data = await getSubjectsSubscriptions({
      PageSize: 400,
      PageNumber: 1,
      IdSubscription: subscription.id,
    });
    subscription.courseSubjects = data?.items as any;
    subscription.planSubjects = data?.items as any;
    try {
      const studentDebtsResponse = await getStudentDebtsBySubscription(
        subscription.id!
      );
      const paymentsReferences = studentDebtsResponse
        .flatMap((sdr) => sdr.paymentReferences || [])
        .filter((ref) => ref?.id)
        .filter((ref) => ref.status);
      subscription.paymentsReferences = paymentsReferences;
      const paymentsTransactions = studentDebtsResponse
        .flatMap((sdr) => sdr.paymentTransactions || [])
        .filter((ref) => ref?.id)
        .filter((ref) => ref.status);
      subscription.paymentsTransactions = paymentsTransactions;
      const studentDebtsAssociations = studentDebtsResponse?.map((sdr) => {
        return {
          idStudentDebt: sdr?.id,
          idSubscription: sdr?.idSubscription,
          idEnrollment: sdr?.idEnrollment,
          price: sdr?.originalAmount,
        };
      });
      subscription.studentDebtsAssociations = studentDebtsAssociations;
      const idsSubjectsArray = Array.from(
        new Set(
          studentDebtsResponse
            ?.map((sdr) => JSON.parse(sdr?.idsSubjects || "[]"))
            .flat()
        )
      );
      subscription.idsSubjectsArray = idsSubjectsArray;
      const studentDebtData = studentDebtsResponse?.[0];
      const paymentReferences = studentDebtData?.paymentReferences;
      const paymentTransactions = studentDebtData?.paymentTransactions;
      const paymentTransaction = paymentTransactions?.find((p) => p?.status);
      const paymentReference = paymentReferences?.find((p) => p?.status);
      subscription.course = subscription?.course;
      subscription.idsSubjects = studentDebtData?.idsSubjects;
      subscription.payment = {
        idEnrollment: studentDebtData?.idEnrollment,
        entity: paymentReference?.entity,
        reference: paymentReference?.referenceCode,
        totalPrice: paymentReference?.amount,
        paymentProof: paymentTransaction?.paymentProof,
        idStudentDebt: studentDebtData?.id,
        isPaymentByEntityReference: true,
      };
      const subjects = subscription.courseSubjects || [];
      const s = subscription;
      const data: any = {
        type: "SUBSCRIPTION",
        outputFormat: "DOCX",
        data: {
          ies: IES.NAME ?? "",
          ano_lectivo: s.academicYear ?? "",
          nome_completo: s.student ?? "",
          codigo: s.studentCode,
          curso: s.course,
          data_emissao: new Date().toLocaleDateString(),
          utilizador: loginData?.Fullname ?? "",
          cargo: loginData?.Role ?? "",
          grades: subjects.map((sub, index) => ({
            ordem: index + 1,
            nome_disciplina: sub.subject,
            nivel: s.level,
            semestre: s.semester
          })),
        },
        logoBase64: logoBase64 ?? ""
      };

      const blob = await exportDocument(data, "SUBSCRIPTION");

    } catch (error) {
      console.log({ error });
    } finally {
      dispatch(resetLoaderAvailable());
    }

    // dispatch(setSubscriptionData(subscription));
    // dispatch(setPdfPreviewType("SUBSCRIPTION"));
    // navigate(routes.pdfPreview);
  };
  const handleEdit = async (subscription: SubscriptionType) => {
    try {
      const studentData = await getStudentById(subscription.idStudent);
      if (!studentData) {
        return;
      }
      const userData = await getUserById(studentData.idUser);
      if (!userData) {
        toast.error("utilizador não encontrado.");
        return;
      }
      const data = await getSubjectsSubscriptions({
        PageSize: 400,
        PageNumber: 1,
        IdSubscription: subscription.id,
      });
      subscription.courseSubjects = data?.items as any;
      subscription.planSubjects = data?.items as any;
      try {
        const studentDebtsResponse = await getStudentDebtsBySubscription(
          subscription.id!
        );
        const paymentsReferences = studentDebtsResponse
          .flatMap((sdr) => sdr.paymentReferences || [])
          .filter((ref) => ref?.id)
          .filter((ref) => ref.status);
        subscription.paymentsReferences = paymentsReferences;
        const paymentsTransactions = studentDebtsResponse
          .flatMap((sdr) => sdr.paymentTransactions || [])
          .filter((ref) => ref?.id)
          .filter((ref) => ref.status);
        subscription.paymentsTransactions = paymentsTransactions;
        const studentDebtsAssociations = studentDebtsResponse?.map((sdr) => {
          return {
            idStudentDebt: sdr?.id,
            idSubscription: sdr?.idSubscription,
            idEnrollment: sdr?.idEnrollment,
            price: sdr?.originalAmount,
          };
        });
        subscription.studentDebtsAssociations = studentDebtsAssociations;
        const idsSubjectsArray = Array.from(
          new Set(
            studentDebtsResponse
              ?.map((sdr) => JSON.parse(sdr?.idsSubjects || "[]"))
              .flat()
          )
        );
        subscription.idsSubjectsArray = idsSubjectsArray;
        const studentDebtData = studentDebtsResponse?.[0];
        const paymentReferences = studentDebtData?.paymentReferences;
        const paymentTransactions = studentDebtData?.paymentTransactions;
        const paymentTransaction = paymentTransactions?.find((p) => p?.status);
        const paymentReference = paymentReferences?.find((p) => p?.status);
        subscription.course = subscription?.course;
        subscription.idsSubjects = studentDebtData?.idsSubjects;
        subscription.payment = {
          idEnrollment: studentDebtData?.idEnrollment,
          entity: paymentReference?.entity,
          reference: paymentReference?.referenceCode,
          totalPrice: paymentReference?.amount,
          paymentProof: paymentTransaction?.paymentProof,
          idStudentDebt: studentDebtData?.id,
          isPaymentByEntityReference: true,
        };
      } catch (error) {
        console.log({ error });
      }
      dispatch(setSubscriptionData(subscription));
      dispatch(setStudentData(studentData));
      dispatch(
        setUserData({
          ...userData,
          isForeign: stringToBoolean(studentData.isForeign),
        } as any)
      );
      navigate(routes.updateSubscription);
    } catch (err: any) {
      toast.error(err?.message);
    }
  };

  const options =
    data?.items.map(() => [
      {
        label: "Editar",
        onClick: (s: SubscriptionType) => handleEdit(s),
        icon: <Pencil1Icon />,
      },
      {
        label: "Excluir",
        onClick: (s: SubscriptionType) => openDeleteDialog(s),
        icon: <TrashIcon />,
      },
      {
        label: "Imprimir",
        onClick: (s: SubscriptionType) => handlePrintSubscription(s),
        icon: <ArchiveIcon />,
      },
    ]) || [];


  const handleLevelChange = async (e: any) => {
    var level = e.target.value;
    setValue("Level", level);
  }
  const handleSemesterChange = async (e: any) => {
    var semester = e.target.value;
    setValue("Semester", semester);
  }

  const handleCourseChange = async (idCourse: string) => {
    setValue('IdCourse', idCourse);
  }

  useEffect(() => {
    if (academicYears?.items && academicYears.items.length > 0) {
      if (!getValues('IdAcademicYear')) {
        const mostRecentYear = academicYears.items[academicYears.items.length - 1];
        setValue('IdAcademicYear', mostRecentYear.id);
      }
    }
  }, [academicYears, setValue, getValues]);



  return (
    <>
      <div style={{ marginBottom: '1rem' }}>

        <Flex gap="2" align="center" style={{ marginBottom: '1rem' }}>
          <Button
            variant="soft"
            onClick={() => setShowFilters(!showFilters)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <MixerHorizontalIcon />
            Filtros
            {showFilters ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </Button>

          <Button
            variant="ghost"
            color="gray"
            onClick={() => { reset(); }}
          >
            Limpar filtros
          </Button>
        </Flex>

        {showFilters && (
          <Card style={{ marginBottom: '1rem', padding: '1rem' }}>
            <Flex direction="column" gap="3">
              <Text size="2" weight="bold">Filtrar inscrições</Text>
              <Flex gap="4">
                <SelectComponent name="IdAcademicYear" label='Ano Acadêmico' control={control} errors={errors} style={{ width: '100%' }}
                  options={academicYears?.items.map(ac => ({
                    value: ac.id ?? "",
                    label: ac.year ?? ""
                  }))}
                  itemsError={errorAcademicYears}
                  itemsLoading={isLoadingAcademicYears}
                />
                <TextFieldComponent
                  name="Level"
                  label="Ano"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: 'Ano', onChange: handleLevelChange, type: 'number' }} style={{ width: '100%' }}
                />
                <TextFieldComponent
                  name="Semester"
                  label="Semestre"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: 'Semestre', onChange: handleSemesterChange, type: 'number' }} style={{ width: '100%' }}

                />

                <SelectComponent name="IdCourse" label='Curso' control={control} errors={errors} style={{ width: '100%' }}
                  options={courses?.items.map(course => ({
                    value: course.id ?? "",
                    label: course.name ?? ""
                  }))}
                  onSelect={
                    handleCourseChange
                  }
                  itemsError={errorCourses}
                  itemsLoading={isLoadingCourses}

                />

              </Flex>
            </Flex>
          </Card>
        )}
      </div>
      <Lists
        status={status}
        TableComponent={() => (
          <TableComponent
            columns={Object.keys(columnNamesMap)}
            items={data?.items}
            options={options}
            columnNamesMap={columnNamesMap}
            totalPages={data?.totalPages || 1}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            onSearch={handleSearch}
            search={searchTerm}
            totalItems={data?.totalItems || 0}
          />
        )}
        handleClickCreate={handleClickCreate}
      />
      {itemToBeDeleted && (
        <AlertDialog.Root
          open={!!itemToBeDeleted}
          onOpenChange={() => setItemToBeDeleted(null)}
        >
          <AlertDialog.Content maxWidth="450px">
            <AlertDialog.Title>Esta operação é irreversível </AlertDialog.Title>
            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray">
                  Cancelar
                </Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button variant="solid" color="red" onClick={confirmDelete}>
                  Continuar
                </Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>
      )}
    </>
  );
};